import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
} from "@mui/material";

// MUI Icons
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import SimCardDownloadIcon from "@mui/icons-material/SimCardDownload";

// Dropzone
import { useDropzone } from "react-dropzone";

// 預覽相關
import FilePreviewModal from "./FilePreviewer";

// API
import { emissionFactorManagementAPI } from "../../../EmissionFactorManagement/API/emissionFactorManagementAPI";
import { dataEntryAPI } from "../../API/dataEntryAPI";

// Toast & i18n
import handleAPIError, {
  MESSAGE_CODES,
  showSuccessToast,
  showErrorToast,
} from "../../../services/toast";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
const tSystem = (key, options) =>
  i18n.t(key, {
    ns: "System",
    ...(options && typeof options === "object" ? options : {}),
  });


// ─────────────────────────────────────────────────────────────────
// Helper: Download File
// ─────────────────────────────────────────────────────────────────
const handleDownload = async (fileInfo) => {
  try {
    let blobUrl = "";
    let downloadName = "";

    if (fileInfo.file instanceof File) {
      blobUrl = URL.createObjectURL(fileInfo.file);
      downloadName = fileInfo.name;
    } else {
      const blob = await dataEntryAPI.HandleDownloadFileAPI(fileInfo.path);
      blobUrl = URL.createObjectURL(blob);
      downloadName = `${fileInfo.name}.${fileInfo.extension}`;
    }

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("下載失敗:", error);
    handleAPIError(error, { throwError: false });
  }
};

// ─────────────────────────────────────────────────────────────────
// ZipDropzone — ZIP / RAR 上傳區（比照 CustomEFValidationFileModal）
// ─────────────────────────────────────────────────────────────────
const ZIP_MAX_SIZE = 500 * 1024 * 1024;

const ZipDropzone = ({ files, setFiles, oldFiles, setOldFiles, t }) => {
  const { getRootProps, isDragActive } = useDropzone({
    maxSize: ZIP_MAX_SIZE,
    multiple: false,
    noClick: true,
    accept: {
      "application/zip": [".zip"],
      "application/x-rar-compressed": [".rar"],
    },
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        const isInvalidType = fileRejections.some((r) =>
          r.errors.some((e) => e.code === "file-invalid-type"),
        );
        if (isInvalidType) {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
            i18nOptions: { types: ".zip, .rar" },
            fallback: tSystem("Please upload files in .zip or .rar format."),
          });
          return;
        }
        showErrorToast({
          i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
          i18nOptions: { size: ZIP_MAX_SIZE / (1024 * 1024) },
          fallback: tSystem(
            `This file exceeds ${ZIP_MAX_SIZE / (1024 * 1024)}MB.`,
          ),
        });
        return;
      }
      if (acceptedFiles[0]) {
        if (oldFiles?.length > 0) setOldFiles([]);
        setFiles([acceptedFiles[0]]);
      }
    },
  });

  const handleFileSelect = (event) => {
    const f = event.target.files[0];
    if (!f) return;
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    if (![".zip", ".rar"].includes(ext)) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
        i18nOptions: { types: ".zip, .rar" },
        fallback: tSystem("Only .zip or .rar files are accepted."),
      });
      return;
    }
    if (f.size > ZIP_MAX_SIZE) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
        i18nOptions: { size: ZIP_MAX_SIZE / (1024 * 1024) },
        fallback: tSystem(
          `This file exceeds ${ZIP_MAX_SIZE / (1024 * 1024)}MB.`,
        ),
      });
      return;
    }
    if (oldFiles?.length > 0) setOldFiles([]);
    setFiles([f]);
    event.target.value = "";
  };

  return (
    <Box>
      <Typography sx={{ mb: 1 }}>
        {t("Upload Supporting Document Single File Only")}
        <br />
        <span style={{ color: "grey" }}>{t("Maximum Size 500MB")}</span>
      </Typography>
      <Paper
        {...getRootProps()}
        sx={{
          border: "2px dashed gray",
          borderRadius: 2,
          padding: 3,
          textAlign: "center",
          cursor: "default",
          bgcolor: isDragActive ? "lightblue" : "background.paper",
        }}
      >
        <FolderZipIcon color="warning" sx={{ fontSize: "56px" }} />
        <Typography variant="body1">
          {isDragActive ? t("Release the file to upload") : t("Choose a file")}{" "}
          (.zip or .rar)
        </Typography>
        <Button
          variant="contained"
          component="label"
          sx={{ ml: 2, mt: 1, textTransform: "none" }}
        >
          {t("Select File")}
          <input
            type="file"
            accept=".zip,.rar"
            onChange={handleFileSelect}
            hidden
          />
        </Button>
      </Paper>

      {/* 已存在的舊檔案 */}
      {oldFiles?.length > 0 && (
        <List sx={{ mt: 2, bgcolor: "#f1f8e9", borderRadius: 2 }}>
          {oldFiles.map((file, idx) => (
            <ListItem
              key={idx}
              secondaryAction={
                <>
                  <Tooltip title={t("Download")} arrow>
                    <IconButton
                      edge="end"
                      color="success"
                      onClick={() =>
                        handleDownload({
                          path: file.path,
                          name: file.fileName,
                          extension: file.extension,
                        })
                      }
                    >
                      <SimCardDownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("Delete")} arrow>
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => {
                        setOldFiles([]);
                        showSuccessToast({ scenarioCode: MESSAGE_CODES.FILE_REMOVE_PENDING_SAVE });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              }
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <InsertDriveFileIcon />
              </ListItemIcon>
              <ListItemText primary={`${file.fileName}.${file.extension}`} />
            </ListItem>
          ))}
        </List>
      )}

      {/* 本次選的新檔案 */}
      {files.length > 0 && (
        <List sx={{ mt: 2, bgcolor: "background.paper", borderRadius: 2 }}>
          {files.map((file, idx) => (
            <ListItem
              key={idx}
              secondaryAction={
                <>
                  <Tooltip title={t("Download")} arrow>
                    <IconButton
                      edge="end"
                      color="success"
                      onClick={() =>
                        handleDownload({ file: file, name: file.name })
                      }
                    >
                      <SimCardDownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("Delete")} arrow>
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => setFiles([])}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              }
            >
              <ListItemIcon>
                <InsertDriveFileIcon />
              </ListItemIcon>
              <ListItemText
                primary={file.name}
                secondary={`${(file.size / 1024).toFixed(2)} KB`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────
// ListLeft — 多檔上傳區（比照 CustomEFValidationFileModal）
// ─────────────────────────────────────────────────────────────────
const MULTI_MAX_SIZE = 3 * 1024 * 1024;
const MULTI_MAX_FILES = 5;

const ListLeft = ({ files, setFiles, oldFiles, setOldFiles, onPreview, t }) => {
  const { getRootProps, isDragActive } = useDropzone({
    maxSize: MULTI_MAX_SIZE,
    noClick: true,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "image/*": [".jpg", ".jpeg", ".png", ".gif"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    onDrop: (acceptedFiles, fileRejections) => {
      if (oldFiles.length + files.length + acceptedFiles.length > MULTI_MAX_FILES) {
        showErrorToast({
          i18nKey: MESSAGE_CODES.FILE_MAX_COUNT_EXCEEDED,
          i18nOptions: { count: MULTI_MAX_FILES },
          fallback: tSystem(
            `You can upload a maximum of ${MULTI_MAX_FILES} files in total.`,
          ),
        });
        return;
      }
      if (fileRejections.length > 0) {
        const names = fileRejections.map((r) => r.file.name);
        if (names.some((n) => n.endsWith(".zip") || n.endsWith(".rar"))) {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
            i18nOptions: { types: "PDF, XLSX, DOCX, JPG, JPEG, PNG, GIF" },
            fallback: tSystem("Zip and Rar files are not allowed."),
          });
        } else if (
          fileRejections.some((r) =>
            r.errors.some((e) => e.code === "file-invalid-type"),
          )
        ) {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
            i18nOptions: { types: "PDF, XLSX, DOCX, JPG, JPEG, PNG, GIF" },
            fallback: tSystem("Unsupported file type."),
          });
        } else {
          showErrorToast({
            i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
            i18nOptions: { size: MULTI_MAX_SIZE / (1024 * 1024) },
            fallback: tSystem(
              `The selected file exceeds ${MULTI_MAX_SIZE / (1024 * 1024)}MB.`,
            ),
          });
        }
      }
      const remaining = MULTI_MAX_FILES - oldFiles.length - files.length;
      setFiles((prev) => [...prev, ...acceptedFiles.slice(0, remaining)]);
    },
  });

  const handleFileSelect = (event) => {
    const selected = Array.from(event.target.files);
    if (selected.some((file) => !/\.(pdf|xlsx|docx|jpg|jpeg|png|gif)$/i.test(file.name))) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_TYPE_NOT_ALLOWED,
        i18nOptions: { types: "PDF, XLSX, DOCX, JPG, JPEG, PNG, GIF" },
      });
      event.target.value = "";
      return;
    }
    if (oldFiles.length + files.length + selected.length > MULTI_MAX_FILES) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_MAX_COUNT_EXCEEDED,
        i18nOptions: { count: MULTI_MAX_FILES },
        fallback: tSystem(
          `You can upload a maximum of ${MULTI_MAX_FILES} files in total.`,
        ),
      });
      event.target.value = "";
      return;
    }
    const valid = selected.filter((f) => f.size <= MULTI_MAX_SIZE);
    const rejected = selected.filter((f) => f.size > MULTI_MAX_SIZE);
    if (rejected.length > 0) {
      showErrorToast({
        i18nKey: MESSAGE_CODES.FILE_SIZE_EXCEEDED,
        i18nOptions: { size: MULTI_MAX_SIZE / (1024 * 1024) },
        fallback: tSystem(
          `The selected file exceeds ${MULTI_MAX_SIZE / (1024 * 1024)}MB.`,
        ),
      });
    }
    setFiles((prev) => [...prev, ...valid]);
    event.target.value = "";
  };

  const totalFiles = oldFiles.length + files.length;

  return (
    <>
      {/* 左欄：已上傳檔案列表 */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography>
          {t("Uploaded file list") + " "}
          <span style={{ color: "grey" }}>({t("up to 5 files allowed")})</span>
        </Typography>

        {/* 伺服器端的舊檔案 */}
        {oldFiles?.length > 0 && (
          <List sx={{ mt: 2, mr: 2, bgcolor: "#f1f8e9", borderRadius: 2 }}>
            {oldFiles.map((file, idx) => (
              <ListItem
                key={idx}
                sx={{ py: 0.8 }}
                secondaryAction={
                  <>
                    <Tooltip title={t("Download")} arrow>
                      <IconButton
                        edge="end"
                        color="success"
                        onClick={() =>
                          handleDownload({
                            path: file.path,
                            name: file.fileName,
                            extension: file.extension,
                          })
                        }
                      >
                        <SimCardDownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Preview")} arrow>
                      <IconButton
                        edge="end"
                        color="primary"
                        onClick={() =>
                          onPreview({
                            path: file.path,
                            name: file.fileName,
                            extension: file.extension,
                          })
                        }
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Delete")} arrow>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => {
                          setOldFiles((prev) =>
                            prev.filter((f) => f.id !== file.id),
                          );
                          showSuccessToast({ scenarioCode: MESSAGE_CODES.FILE_REMOVE_PENDING_SAVE });
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                }
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <InsertDriveFileIcon />
                </ListItemIcon>
                <ListItemText primary={`${file.fileName}.${file.extension}`} />
              </ListItem>
            ))}
          </List>
        )}

        {/* 本次選的新檔案 */}
        {files.length > 0 && (
          <List sx={{ bgcolor: "background.paper", borderRadius: 2 }} dense>
            {files.map((file, idx) => (
              <ListItem
                key={idx}
                secondaryAction={
                  <>
                    <Tooltip title={t("Download")} arrow>
                      <IconButton
                        edge="end"
                        color="success"
                        onClick={() =>
                          handleDownload({ file: file, name: file.name })
                        }
                      >
                        <SimCardDownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Preview")} arrow>
                      <IconButton
                        edge="end"
                        color="primary"
                        onClick={() =>
                          onPreview({ file: file, name: file.name })
                        }
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Delete")} arrow>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() =>
                          setFiles((prev) => prev.filter((_, i) => i !== idx))
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                }
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <InsertDriveFileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                />
              </ListItem>
            ))}
          </List>
        )}

        {oldFiles.length === 0 && files.length === 0 && (
          <Typography
            sx={{ color: "#aaa", fontSize: "14px", mt: 3, textAlign: "center" }}
          >
            {t("No files attached yet.")}
          </Typography>
        )}
      </Grid>

      {/* 右欄：上傳區 */}
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{ borderLeft: "2px solid #ccc", height: "100%" }}
      >
        <Box sx={{ m: 1 }}>
          <Typography sx={{ mb: 1 }}>
            {t("Upload Supporting Documents")}
            <br />
            <span style={{ color: "grey" }}>
              {t("Maximum Size Per File")} : 3MB
            </span>
          </Typography>
          <Paper
            {...getRootProps()}
            sx={{
              border: "2px dashed gray",
              borderRadius: 2,
              padding: 3,
              textAlign: "center",
              cursor: "default",
              bgcolor: isDragActive ? "lightblue" : "background.paper",
              opacity: totalFiles >= MULTI_MAX_FILES ? 0.5 : 1,
            }}
          >
            <CloudUploadIcon
              color={totalFiles >= MULTI_MAX_FILES ? "disabled" : "primary"}
              sx={{ fontSize: "56px" }}
            />
            <Typography variant="body1">
              {isDragActive
                ? t("Release the files or folders to upload")
                : t("Choose a file")}
              <br />
              (.xlsx, .pdf, .docx, image/*)
            </Typography>
            <Button
              variant="contained"
              component="label"
              disabled={totalFiles >= MULTI_MAX_FILES}
              sx={{ mt: 1, textTransform: "none" }}
            >
              {t("Select File")}
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                hidden
                accept=".pdf,.xlsx,.docx,.jpg,.png,.jpeg,.gif"
              />
            </Button>
          </Paper>
          {totalFiles >= MULTI_MAX_FILES && (
            <Typography
              sx={{ color: "red", fontSize: "14px", fontWeight: "bold", mt: 2 }}
            >
              {t("You have already selected 5 files.")}
            </Typography>
          )}
        </Box>
      </Grid>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────
// ViewCustomEFAttachModal — 主元件
// Props:
//   open        {boolean}  是否顯示
//   onClose     {function} 關閉 callback（可選配合重新整理）
//   fileList    {Array}    getValidationFileAPI 回傳的伺服器端檔案列表
//   efName      {string}   係數名稱（顯示於 title）
//   customEFId  {string|number}  自定義係數 id
//   companyId   {string}   公司 id
// ─────────────────────────────────────────────────────────────────
const ViewCustomEFAttachModal = ({
  open,
  onClose,
  fileList = [],
  efName = "",
  customEFId,
  companyId,
  readOnly = false,
  creatorName = "",
}) => {
  const { t } = useTranslation("ActivityMain");

  // 判斷初始模式
  const detectMode = (list) => {
    if (
      list.length > 0 &&
      ["zip", "rar"].includes(list[0]?.extension?.toLowerCase())
    )
      return 2;
    return 1;
  };

  const [selectedOption, setSelectedOption] = useState(detectMode(fileList));
  const [oldFiles, setOldFiles] = useState([...fileList]); // 伺服器端的舊檔案
  const [files, setFiles] = useState([]); // 本次新上傳的檔案
  const [originalOldFiles, setOriginalOldFiles] = useState([...fileList]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 預覽 state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // 互鎖邏輯：若目前存在舊檔案或已選取新檔案，則根據目前模式互鎖另一個選項
  const hasAnyFile = oldFiles.length > 0 || files.length > 0;
  const disableMulti = hasAnyFile && selectedOption === 2; // 目前是 ZIP 模式且有檔案，禁用多檔
  const disableZip = hasAnyFile && selectedOption === 1; // 目前是多檔模式且有檔案，禁用 ZIP

  // 開啟時同步初始資料
  useEffect(() => {
    if (open) {
      const mode = detectMode(fileList);
      setSelectedOption(mode);
      setOldFiles([...fileList]);
      setOriginalOldFiles([...fileList]);
      setFiles([]);
      setIsSubmitting(false);
    }
  }, [open]); // eslint-disable-line

  const handleChange = (event) => {
    setSelectedOption(Number(event.target.value));
    setFiles([]);
  };

  const handlePreview = (data) => {
    setPreviewData(data);
    setPreviewOpen(true);
  };

  // Save — 比照 CustomEFValidationFileModal.handleSave
  // 注意：setValidationFileAPI 內部已呼叫 showSuccessToast，此處不重複呼叫
  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const filesId =
      oldFiles.length > 0 ? oldFiles.map((f) => f.id).join(",") : "";

    try {
      // 情況A：所有舊檔被刪除 + 無新檔 → filesId="" + files=[]
      if (
        originalOldFiles.length > 0 &&
        oldFiles.length === 0 &&
        files.length === 0
      ) {
        await emissionFactorManagementAPI.setValidationFileAPI(
          customEFId,
          companyId,
          selectedOption,
          "",
          [],
        );
        onClose(true);
        return;
      }

      // 情況B：所有舊檔被刪除 + 有新檔 → filesId="" + files=新檔
      if (
        originalOldFiles.length > 0 &&
        oldFiles.length === 0 &&
        files.length > 0
      ) {
        await emissionFactorManagementAPI.setValidationFileAPI(
          customEFId,
          companyId,
          selectedOption,
          "",
          files,
        );
        onClose(true);
        return;
      }

      // 偵測舊檔是否有部分變動（刪了某幾筆）
      const hasOldFileChanged = oldFiles.length !== originalOldFiles.length;

      // 情況C：無任何變更（舊檔未動，也沒新增）→ 直接關閉
      if (!hasOldFileChanged && files.length === 0) {
        onClose(false);
        return;
      }

      // 情況D：部分舊檔刪除 或 有新檔上傳 → 呼叫 API
      // filesId 帶入「剩餘的舊檔 id」，告知後端保留哪些；files 帶入新檔（可為空陣列）
      await emissionFactorManagementAPI.setValidationFileAPI(
        customEFId,
        companyId,
        selectedOption,
        filesId,
        files,
      );
      onClose(true);
    } catch (err) {
      console.error("Error uploading validation files", err);
      handleAPIError(err, { throwError: false });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (readOnly) {
    return (
      <>
        <Dialog
          open={open}
          onClose={() => onClose(false)}
          maxWidth="sm"
          fullWidth
          sx={{ zIndex: 10000 }}
        >
          <DialogTitle>
            <IconButton
              onClick={() => onClose(false)}
              sx={{ position: "absolute", right: 8, top: 10 }}
            >
              <CloseIcon />
            </IconButton>
            {t("Supporting Documents For")} {efName}
          </DialogTitle>
          <DialogContent dividers>
            <Typography
              sx={{ mb: 2, p: 1.5, bgcolor: "#f3f6fa", borderRadius: 1, fontSize: 13 }}
            >
              {creatorName ? `${t("Shared by")} ${creatorName}` : t("Shared")}
            </Typography>
            {oldFiles.length === 0 ? (
              <Typography sx={{ py: 8, color: "#788392", textAlign: "center" }}>
                {t("No supporting documents available.")}
              </Typography>
            ) : (
              <List dense>
                {oldFiles.map((file) => (
                  <ListItem
                    key={file.id ?? file.path}
                    secondaryAction={
                      <>
                        <Tooltip title={t("Download")} arrow>
                          <IconButton
                            color="success"
                            onClick={() => handleDownload({
                              path: file.path,
                              name: `${file.fileName}.${file.extension}`,
                            })}
                          >
                            <SimCardDownloadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("Preview")} arrow>
                          <IconButton
                            color="primary"
                            onClick={() => handlePreview({
                              path: file.path,
                              name: file.fileName,
                              extension: file.extension,
                            })}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    }
                  >
                    <ListItemIcon><InsertDriveFileIcon /></ListItemIcon>
                    <ListItemText primary={`${file.fileName}.${file.extension}`} />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose(false)} variant="contained">
              {t("Close")}
            </Button>
          </DialogActions>
        </Dialog>
        <FilePreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          {...previewData}
        />
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose(false)}
        maxWidth={false}
        sx={{ zIndex: 10000 }}
        PaperProps={{
          sx: {
            height: "550px",
            width: selectedOption === 2 ? "500px" : "800px",
          },
        }}
      >
        {/* Title */}
        <DialogTitle>
          <IconButton
            onClick={() => onClose(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 10,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          {t("Upload Consumption Data For")} {efName}
        </DialogTitle>

        {/* Radio 切換：多檔 / ZIP */}
        <FormControl sx={{ px: 3 }}>
          <RadioGroup row value={selectedOption} onChange={handleChange}>
            <FormControlLabel
              value={1}
              control={<Radio />}
              label={t("Upload multiple files")}
              sx={{ marginRight: 15 }}
              disabled={disableMulti}
            />
            <FormControlLabel
              value={2}
              control={<Radio />}
              label={t("Upload zip file")}
              disabled={disableZip}
            />
          </RadioGroup>
        </FormControl>

        <DialogContent sx={{ overflow: "hidden" }}>
          {selectedOption === 2 ? (
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              alignItems="flex-end"
            >
              <Grid size={12}>
                <ZipDropzone
                  files={files}
                  setFiles={setFiles}
                  oldFiles={oldFiles}
                  setOldFiles={setOldFiles}
                  t={t}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid
              container
              spacing={2}
              justifyContent="space-around"
              alignItems="flex-start"
              height="100%"
            >
              <ListLeft
                files={files}
                setFiles={setFiles}
                oldFiles={oldFiles}
                setOldFiles={setOldFiles}
                onPreview={handlePreview}
                t={t}
              />
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => onClose(false)}
            variant="contained"
            sx={{ bgcolor: "#e0e0e0", color: "#000", textTransform: "none" }}
          >
            {t("Close")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSubmitting}
            sx={{ textTransform: "none" }}
          >
            {isSubmitting ? t("Save...") : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 路徑預覽 Modal */}
      <FilePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        {...previewData}
      />
    </>
  );
};

export default ViewCustomEFAttachModal;
